import { UserBio } from '../../types';

/**
 * Transforms user bio data into a human-readable format for AI consumption
 */
export const formatUserBioForAI = (bio: UserBio): string => {
  let formatted = `User Profile:\n\n`;

  // Basic information
  formatted += `Background: ${bio.background}\n`;
  formatted += `Business Type: ${bio.businessType}\n`;
  formatted += `Time Available: ${bio.timeAvailable}\n`;
  formatted += `Comfortable Learning: ${bio.comfortableLearning ? 'Yes' : 'No'}\n\n`;

  // Spaces/Interests
  formatted += `Areas of Interest:\n`;
  formatted += `- General Spaces: ${bio.spaces.join(', ')}\n`;
  if (bio.spacesSpecific && bio.spacesSpecific.length > 0) {
    formatted += `- Specific Spaces: ${bio.spacesSpecific.join(', ')}\n`;
  }

  // Areas/Skills
  formatted += `\nAreas of Expertise:\n`;
  formatted += `- General Areas: ${bio.areas.join(', ')}\n`;
  if (bio.areasSpecific && bio.areasSpecific.length > 0) {
    formatted += `- Specific Areas: ${bio.areasSpecific.join(', ')}\n`;
  }

  // Additional context
  if (bio.hasCofounders !== undefined) {
    formatted += `\nTeam: ${bio.hasCofounders ? 'Will have cofounders' : 'Solo founder'}\n`;
  }

  return formatted;
};

/**
 * Creates a structured prompt for business idea generation
 */
export const createBusinessIdeasPrompt = (userBio: UserBio, numIdeas: number = 5): string => {
  const formattedBio = formatUserBioForAI(userBio);

  return `You are an expert business consultant and startup advisor. Based on the following user profile, generate ${numIdeas} innovative business ideas that align with their background, interests, and constraints.

${formattedBio}

Please provide ${numIdeas} business ideas in the following JSON format:

{
  "ideas": [
    {
      "title": "Business Idea Title",
      "description": "Brief description of the business concept",
      "targetMarket": "Who this business serves",
      "revenueModel": "How the business makes money",
      "timeToLaunch": "Estimated time to launch (e.g., '3-6 months', '6-12 months')",
      "complexity": "low|medium|high",
      "requiredSkills": ["skill1", "skill2"],
      "estimatedInvestment": "Low|Medium|High",
      "whyItFits": "Why this idea specifically fits this user's profile"
    }
  ]
}

Focus on ideas that:
- Match the user's background and expertise
- Fit within their time availability constraints
- Align with their comfort level for learning new skills
- Leverage their specific areas of interest and expertise
- Are realistic given their team situation (solo vs cofounders)

Make sure each idea is actionable and specific, not just general concepts.`;
};

/**
 * Cleans raw user bio data by removing form-specific fields and applying business logic
 */
export const cleanUserBioData = (rawUserBio: any): UserBio => {
  const cleanedUserBio: UserBio = {
    background: rawUserBio.background,
    businessType: rawUserBio.businessType,
    timeAvailable: rawUserBio.timeAvailable,
    comfortableLearning: rawUserBio.comfortableLearning,
    spaces: rawUserBio.spaces,
    areas: rawUserBio.areas,
  };

  // Add conditional fields only if they exist and complexity is advanced
  if (rawUserBio.complexity === 'advanced') {
    if (rawUserBio.hasCofounders !== undefined) {
      cleanedUserBio.hasCofounders = rawUserBio.hasCofounders;
    }
    if (rawUserBio.spacesSpecific && rawUserBio.spacesSpecific.length > 0) {
      cleanedUserBio.spacesSpecific = rawUserBio.spacesSpecific;
    }
    if (rawUserBio.areasSpecific && rawUserBio.areasSpecific.length > 0) {
      cleanedUserBio.areasSpecific = rawUserBio.areasSpecific;
    }
  }

  return cleanedUserBio;
};
